import useGetPipelineStageInfiniteTargetingAutomations from '@/api/queries/pipelines/useGetPipelineStageInfiniteTargetingAutomations';
import { useAutomationDescriptions } from '@/hooks/useAutomationDescriptions';
import { usePermissions } from '@/hooks/usePermissions';
import useDraftDeleteAutomation from '@/ui/pages/pipelineConfigPage/api/mutations/useDraftDeleteAutomation';
import TargetingAutomationCard from '@/ui/pages/pipelineConfigPage/components/automations/TargetingAutomationCard';
import { Card, DividerWithLabel, SkeletonLoader } from '@payaca/ambrosia';
import { AutomationsPermissions } from '@payaca/permissions/automations/automations.permissions';

import { useDraftMode } from '@/ui/pages/pipelineConfigPage/context/DraftModeContext';
import { useObserveIntersection } from '@payaca/hooks/useObserveIntersection';
import { FC, Fragment } from 'react';

const PipelineStageTargetingAutomations: FC<{
  pipelinePublicId: string;
  pipelineStagePublicId: string;
}> = ({ pipelinePublicId, pipelineStagePublicId }) => {
  const { pendingCount } = useDraftMode();
  const { userHasRequiredPermission } = usePermissions();
  const canEditAutomation = userHasRequiredPermission([
    AutomationsPermissions.EDIT_AUTOMATION,
  ]);

  const { mutate: deleteAutomation } = useDraftDeleteAutomation({
    pipelinePublicId,
    pipelineStagePublicId,
  });

  const {
    getTriggerDescriptionForAutomation,
    getConditionDescription,
    getActionDescription,
  } = useAutomationDescriptions();

  const {
    targetingAutomations,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useGetPipelineStageInfiniteTargetingAutomations({
    pipelinePublicId,
    pipelineStagePublicId,
  });

  const loaderRef = useObserveIntersection(
    () => hasNextPage && !isFetchingNextPage && void fetchNextPage()
  );

  return (
    <>
      {targetingAutomations?.map((automation) => (
        <Fragment key={automation.id}>
          <DividerWithLabel label="or" />
          <TargetingAutomationCard
            title={getTriggerDescriptionForAutomation(
              automation.entityType,
              automation.triggerEvent
            )}
            to={{
              pathname: `/automations/${automation.privateId}`,
              search: `?from=pipeline&pipelineId=${pipelinePublicId}`,
            }}
            disableTo={pendingCount > 0}
            showEditButton={canEditAutomation}
            showDeleteButton={!canEditAutomation}
            onDelete={() => deleteAutomation({ automationId: automation.id })}
            conditions={automation.conditions
              .filter((c) => c.__typename !== 'ProjectPipelineCondition')
              .map(getConditionDescription)}
            additionalActions={automation.actions
              .filter(
                (a) =>
                  a.__typename !== 'MoveProjectToPipelineStageAutomationAction'
              )
              .map(getActionDescription)}
          />
        </Fragment>
      ))}
      {hasNextPage && (
        <>
          <DividerWithLabel label="or" />
          <Card sizeVariant="sm" ref={loaderRef}>
            <Card.Body>
              <SkeletonLoader.Title />
            </Card.Body>
          </Card>
        </>
      )}
    </>
  );
};

export default PipelineStageTargetingAutomations;
